import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, MapPin } from 'lucide-react';

// Fix for default marker icon in Vite/Leaflet
// @ts-ignore
import markerIcon from 'leaflet/dist/images/marker-icon.png';
// @ts-ignore
import markerIconRetina from 'leaflet/dist/images/marker-icon-2x.png';
// @ts-ignore
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIconRetina,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface LocationPickerProps {
    initialLat?: number;
    initialLng?: number;
    onChange: (lat: number, lng: number, addressDetails?: any) => void;
}

function LocationMarker({ position, setPosition, onChange }: {
    position: [number, number],
    setPosition: (pos: [number, number]) => void,
    onChange: (lat: number, lng: number) => void
}) {
    const map = useMapEvents({
        click(e) {
            const newPos: [number, number] = [e.latlng.lat, e.latlng.lng];
            setPosition(newPos);
            onChange(e.latlng.lat, e.latlng.lng);
            map.flyTo(e.latlng, map.getZoom());
        },
    });

    return position === null ? null : (
        <Marker position={position} />
    );
}

function ChangeView({ center }: { center: [number, number] }) {
    const map = useMap();
    useEffect(() => {
        map.setView(center, map.getZoom());
    }, [center, map]);
    return null;
}

export function LocationPicker({ initialLat, initialLng, onChange }: LocationPickerProps) {
    const defaultCenter: [number, number] = [initialLat || -34.6037, initialLng || -58.3816]; // Buenos Aires default
    const [position, setPosition] = useState<[number, number]>(defaultCenter);
    const [isLocating, setIsLocating] = useState(false);

    const handleLocationFound = (lat: number, lng: number) => {
        setPosition([lat, lng]);
        onChange(lat, lng);
    };

    const requestLocation = () => {
        setIsLocating(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    handleLocationFound(pos.coords.latitude, pos.coords.longitude);
                    setIsLocating(false);
                },
                (error) => {
                    console.error("Error getting location", error);
                    setIsLocating(false);
                    alert("No se pudo obtener tu ubicación actual.");
                },
                { enableHighAccuracy: true }
            );
        } else {
            alert("Tu navegador no soporta geolocalización.");
            setIsLocating(false);
        }
    };

    return (
        <div className="relative w-full h-64 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <MapContainer
                center={position}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <LocationMarker position={position} setPosition={setPosition} onChange={onChange} />
                <ChangeView center={position} />
            </MapContainer>

            <button
                type="button"
                onClick={requestLocation}
                disabled={isLocating}
                className="absolute bottom-4 right-4 z-[1000] p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 text-primary hover:bg-gray-50 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
                title="Usar mi ubicación actual"
            >
                {isLocating ? (
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                    <Crosshair className="w-5 h-5" />
                )}
            </button>

            <div className="absolute top-4 left-4 z-[1000] px-3 py-1.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 text-[10px] font-medium text-gray-500 uppercase flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-red-500" />
                Toca el mapa para mover el pin
            </div>
        </div>
    );
}
